import { ProfileModel, ProfileStore, profileStore } from '@entities/profile'
import { sessionRepository, sessionStore } from '@entities/session'
import { Action } from '@reduxjs/toolkit'
import { registerEpic } from '@shared/lib/redux'
import { RouterStore, routerStore } from '@shared/lib/router'
import { StateObservable } from 'redux-observable'
import { concat, filter, from, Observable, of } from 'rxjs'
import { catchError, mergeMap, withLatestFrom } from 'rxjs/operators'

type PartialRootState = {
  [routerStore.name]: RouterStore
  [profileStore.name]: ProfileStore
}

const createSessionEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(sessionStore.actions.createSessionStart.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      return from(
        sessionRepository.create(
          action.payload.username,
          action.payload.password
        )
      ).pipe(
        mergeMap((response) =>
          concat(
            of(sessionStore.actions.createSessionSuccess(response.session)),
            of(
              profileStore.actions.setProfile(
                new ProfileModel(
                  response.info.userFullName,
                  response.info.userRole,
                  response.info.paidUpUntil
                    ? new Date(response.info?.paidUpUntil)
                    : new Date(),
                  response.info?.subDaysRemain ?? null
                )
              )
            ),
            of(routerStore.actions.saveCurrent(state.routerSlice.prev ?? '/'))
          )
        ),
        catchError((error: unknown) => {
          return of(sessionStore.actions.createSessionFailure(error as string))
        })
      )
    })
  )

const checkAndUpdateSessionEpic = (action$: Observable<Action>) =>
  action$.pipe(
    filter(sessionStore.actions.updateSessionStart.match),

    mergeMap((_action) => {
      if (location.pathname === `/signin`) {
        return of(sessionStore.actions.updateSessionCanceled())
      }
      return from(sessionRepository.update()).pipe(
        mergeMap(
          (response) => {
            return concat(
              of(sessionStore.actions.updateSessionSuccess(response.session)),
              of(
                profileStore.actions.setProfile(
                  new ProfileModel(
                    response.info.userFullName,
                    response.info.userRole,
                    response.info.paidUpUntil
                      ? new Date(response.info.paidUpUntil)
                      : new Date(),
                    response.info?.subDaysRemain ?? null
                  )
                )
              )
              // of()
            )
          }
          // EMPTY
        ),
        catchError((error) => {
          const errorText =
            typeof error === 'string' ? error : 'Failed to update session'
          return concat(
            of(sessionStore.actions.updateSessionFailure(errorText)),
            of(profileStore.actions.deleteProfile()),
            of(routerStore.actions.saveCurrent('/signin'))
          )
        })
      )
    })
  )

const removeSessionEpic = (action$: Observable<Action>) =>
  action$.pipe(
    filter(sessionStore.actions.removeSessionStart.match),
    mergeMap(() =>
      from(sessionRepository.remove()).pipe(
        mergeMap(() => {
          return concat(
            of(routerStore.actions.saveCurrent('/signin')),
            of(sessionStore.actions.removeSessionSuccess()),
            of(profileStore.actions.deleteProfile())
          )
        }),
        catchError((error) =>
          concat(
            of(routerStore.actions.saveCurrent('/signin')),
            of(profileStore.actions.deleteProfile()),
            of(sessionStore.actions.removeSessionFailure(error as string))
          )
        )
      )
    )
  )

registerEpic(createSessionEpic)
registerEpic(checkAndUpdateSessionEpic)
registerEpic(removeSessionEpic)
